import { useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Scale } from 'lucide-react';

const TermsAndConditions = () => {
  useEffect(() => {
    document.title = 'Terms and Conditions | Swell Breaks';
  }, []);

  return (
    <Layout>
      <Seo
        title="Terms & Conditions | Swell Breaks"
        description="The terms that govern your use of Swell Breaks."
        path="/terms-and-conditions"
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Scale className="h-4 w-4" />
                Legal
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
                Terms and Conditions
              </h1>
              <p className="text-muted-foreground">
                Last Updated: 23 June 2026
              </p>
            </div>

            <Card className="bg-card/80 backdrop-blur-sm border-border/20">
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-invert max-w-none">
                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">1. Introduction</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>These Terms and Conditions govern your use of the Swell Breaks website, mobile applications, and related services (collectively, the "Services").</p>
                    <p>By accessing or using the Services, you agree to be bound by these Terms.</p>
                    <p>If you do not agree with these Terms, you should not use the Services.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">2. Company Information</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-1 mb-8">
                    <p className="font-semibold text-foreground">Swell Breaks Ltd</p>
                    <p>Company Number: 17118943</p>
                    <p>Email: <a href="mailto:Finn.Turley@swellbreaks.com" className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors">Finn.Turley@swellbreaks.com</a></p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">3. About Swell Breaks</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Swell Breaks is a surf travel discovery and planning platform.</p>
                    <p>We provide destination information, travel inspiration, accommodation recommendations, trip-planning tools, and related content.</p>
                    <p>Swell Breaks is not a travel agent, tour operator, airline, accommodation provider, insurer, surf school, or transportation provider.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">4. User Accounts</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Where account registration is available:</p>
                    <p>You are responsible for:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Maintaining the confidentiality of your login credentials</li>
                      <li>Ensuring information provided is accurate</li>
                      <li>All activity occurring under your account</li>
                    </ul>
                    <p>We reserve the right to suspend or terminate accounts that breach these Terms.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">5. Destination Information</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We strive to provide accurate information.</p>
                    <p>However, we do not guarantee the accuracy, completeness, or suitability of any information provided through the platform.</p>
                    <p>Surf conditions, travel requirements, accommodation availability, pricing, and local circumstances can change without notice.</p>
                    <p>Users should independently verify information before making travel decisions.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">6. No Professional Advice</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Content provided through Swell Breaks is for informational purposes only.</p>
                    <p>Nothing on the platform constitutes:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Travel advice</li>
                      <li>Safety advice</li>
                      <li>Legal advice</li>
                      <li>Medical advice</li>
                      <li>Insurance advice</li>
                    </ul>
                    <p>Users remain responsible for their own decisions.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">7. Surfing and Adventure Activity Disclaimer</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Surfing and adventure travel involve inherent risks.</p>
                    <p>These risks may include:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Injury</li>
                      <li>Drowning</li>
                      <li>Illness</li>
                      <li>Equipment failure</li>
                      <li>Wildlife encounters</li>
                      <li>Weather events</li>
                      <li>Travel disruption</li>
                    </ul>
                    <p>By using our Services, you acknowledge these risks and accept responsibility for your participation in such activities.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">8. Third-Party Services</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>The platform may contain links to third-party providers.</p>
                    <p>Any booking, purchase, reservation, or transaction made with a third party is governed solely by that provider's terms and conditions.</p>
                    <p>We are not responsible for:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Supplier performance</li>
                      <li>Booking errors</li>
                      <li>Cancellations</li>
                      <li>Refund disputes</li>
                      <li>Service quality</li>
                    </ul>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">9. Affiliate Disclosure</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Some links on Swell Breaks may be affiliate links.</p>
                    <p>If you make a purchase through an affiliate link, we may receive a commission at no additional cost to you.</p>
                    <p>Affiliate relationships do not influence our commitment to providing useful and relevant recommendations.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">10. Intellectual Property</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Unless otherwise stated, all content on the platform, including:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Branding</li>
                      <li>Logos</li>
                      <li>Designs</li>
                      <li>Databases</li>
                      <li>Text</li>
                      <li>Graphics</li>
                      <li>Software</li>
                    </ul>
                    <p>is owned by or licensed to Swell Breaks and protected by intellectual property laws.</p>
                    <p>You may not reproduce, distribute, or commercially exploit content without written permission.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">11. User Content</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Where users submit reviews, comments, images, or other content:</p>
                    <p>You retain ownership of your content.</p>
                    <p>By submitting content, you grant Swell Breaks a non-exclusive, worldwide, royalty-free licence to use, display, reproduce, and distribute that content in connection with the Services.</p>
                    <p>You must not submit content that is unlawful, defamatory, abusive, misleading, or infringes intellectual property rights.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">12. Acceptable Use</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>You agree not to:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Interfere with platform functionality</li>
                      <li>Attempt unauthorised access</li>
                      <li>Introduce malware</li>
                      <li>Use automated scraping tools without permission</li>
                      <li>Violate applicable laws</li>
                    </ul>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">13. Limitation of Liability</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>To the maximum extent permitted by law, Swell Breaks shall not be liable for:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Indirect losses</li>
                      <li>Consequential losses</li>
                      <li>Lost profits</li>
                      <li>Travel disruption</li>
                      <li>Personal injury</li>
                      <li>Property damage</li>
                      <li>Booking losses</li>
                    </ul>
                    <p>arising from the use of the Services.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">14. Indemnity</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>You agree to indemnify and hold harmless Swell Breaks, its directors, employees, contractors, and affiliates against claims arising from:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Your use of the Services</li>
                      <li>Breach of these Terms</li>
                      <li>Violation of applicable laws</li>
                    </ul>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">15. Suspension and Termination</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We reserve the right to suspend or terminate access to the Services where:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>These Terms are breached</li>
                      <li>Fraudulent activity is suspected</li>
                      <li>Security concerns arise</li>
                    </ul>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">16. Changes to the Services</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We may modify, suspend, or discontinue any aspect of the Services at any time without liability.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">17. Privacy</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Use of the Services is also governed by our <a href="/privacy-policy" className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors">Privacy Policy</a>.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">18. Governing Law</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>These Terms are governed by the laws of England and Wales.</p>
                    <p>Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
                  </div>
                </div>

                <Separator className="my-10 bg-border/30" />

                <div className="text-center text-sm text-muted-foreground">
                  <p className="mb-1">If you have any questions about these Terms, please contact us at</p>
                  <a
                    href="mailto:Finn.Turley@swellbreaks.com"
                    className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
                  >
                    Finn.Turley@swellbreaks.com
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TermsAndConditions;
